import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUSES = [
  {
    label: "STATUS: THE REPETITION LOOP",
    body: "Exhausted from repeating the exact same instructions to your kids every single morning with zero behavior change?",
  },
  {
    label: "STATUS: THE DICTATOR TRAP",
    body: "Forced into playing the household enforcer, constantly nagging and chasing family members down instead of leading?",
  },
  {
    label: "STATUS: THE ATTENTION APATHY",
    body: "Are real-world chores, critical routines, and healthy habits actively losing the war to digital screens and passive drifting?",
  },
];

const RESCUE = [
  {
    n: "01",
    title: "AUTOMATED ACCOUNTABILITY",
    body: "STAKES completely de-escalates parenting friction. We don't just log chores; we build a cold, mechanical environment that replaces verbal reminders with high-contrast visual reality.",
  },
  {
    n: "02",
    title: "THE NEUTRAL ENFORCER",
    body: "By shifting the rules to a permanent kitchen tablet or wall display, emotional pushback is eliminated. The code is locked. The digital proof is required. The points and penalties are automatic.",
  },
  {
    n: "03",
    title: "ELITE FAMILY PERFORMANCE",
    body: "We turn basic daily friction into a high-stakes accountability game. It forces the entire family unit to synchronize, communicate, and execute together like an elite team.",
  },
];

export default function ManifestoModal({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-6xl w-[98vw] max-h-[95vh] overflow-y-auto bg-[#0A0A0A] border-2 border-[#2A2A2A] rounded-none p-0 [&>button]:hidden"
        data-testid="manifesto-modal"
      >
        <DialogTitle className="sr-only">The Manifesto</DialogTitle>
        <DialogDescription className="sr-only">Why STAKES exists</DialogDescription>

        {/* Title bar */}
        <div className="px-5 md:px-10 pt-8 pb-4 border-b border-[#2A2A2A] flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">THE WHY</div>
            <h2 className="font-anton text-5xl md:text-6xl uppercase tracking-tight leading-[0.9] mt-2 text-white">
              The Manifesto
            </h2>
          </div>
          <button
            onClick={onClose}
            className="label-mono px-3 h-10 border border-[#2A2A2A] hover:bg-white hover:text-black hover-flip"
            data-testid="manifesto-close-button"
          >
            ✕ CLOSE
          </button>
        </div>

        {/* Two-column manifesto grid */}
        <div className="grid md:grid-cols-2 gap-px bg-[#2A2A2A]">
          {/* LEFT — THE AUDIT */}
          <section
            className="bg-[#121212] p-6 md:p-10 relative"
            style={{ boxShadow: "inset 0 0 80px rgba(255,59,48,0.10)" }}
            data-testid="manifesto-audit"
          >
            <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">LEFT / 01</div>
            <h3 className="font-anton text-5xl uppercase tracking-tight mt-2 leading-[0.9] text-white">
              The Audit
            </h3>

            <div className="mt-6 border-l-2 border-[#FF3B30] pl-4">
              <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">THE MISSION</div>
              <p
                className="text-lg text-white mt-2 leading-snug wrap-words"
                style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
              >
                STAKES is a high-contrast, family habit-forming challenge game designed to replace
                daily friction with automated accountability.
              </p>
            </div>

            <div className="mt-8 space-y-6">
              {STATUSES.map((s, i) => (
                <div key={s.label} data-testid={`manifesto-status-${i}`}>
                  <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">
                    [{s.label}]
                  </div>
                  <p
                    className="text-lg text-white mt-2 leading-snug wrap-words"
                    style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
                  >
                    {s.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RIGHT — THE SYSTEMIC RESCUE */}
          <section className="bg-[#0A0A0A] p-6 md:p-10" data-testid="manifesto-rescue">
            <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">RIGHT / 02</div>
            <h3 className="font-anton text-5xl uppercase tracking-tight mt-2 leading-[0.9] text-white">
              The Systemic<br/>Rescue
            </h3>

            <div className="mt-8 space-y-8">
              {RESCUE.map((r) => (
                <div key={r.n} className="border-t border-[#2A2A2A] pt-5" data-testid={`manifesto-rescue-${r.n}`}>
                  <div className="flex items-baseline gap-4">
                    <div className="font-anton text-4xl text-[#FF3B30] leading-none">{r.n}</div>
                    <div className="font-mono text-xs text-[#FF3B30] tracking-[0.2em] font-bold">/</div>
                    <h4 className="font-anton text-2xl md:text-3xl uppercase tracking-tight text-white leading-none">
                      {r.title}
                    </h4>
                  </div>
                  <p
                    className="text-lg text-white mt-3 leading-snug wrap-words"
                    style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
                  >
                    {r.body}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="px-5 md:px-10 py-6 border-t border-[#2A2A2A]">
          <button
            onClick={onClose}
            className="w-full h-14 border-2 border-[#FF3B30] text-[#FF3B30] font-anton text-xl uppercase tracking-tight hover:bg-[#FF3B30] hover:text-black hover-flip"
            data-testid="manifesto-cta-button"
          >
            ENOUGH TALK. LET&apos;S RUN IT →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
